import { useMutation, useQuery } from '@tanstack/react-query';
import { SESSION_KINDS, SESSION_KIND_LABELS, type SessionKind, type SessionParticipant } from '@raro/domain';
import { Button, EmptyState, Field, Input, Label, Notice, Select, Tag, Textarea, cx } from '@raro/ui';
import { useState } from 'react';
import { Link } from 'react-router';
import { useAiStatus } from '../lib/ai';
import { api } from '../lib/api';
import { SOURCE_STATUS_LABELS, SOURCE_TYPE_LABELS, formatDate, sourceIsPending } from '../lib/format';
import type { Session, SessionDetail, Source } from '../lib/types';
import { AddSourceDrawer } from './AddSourceDrawer';
import { ConversationsPanel } from './ConversationsPanel';
import { MachineFill } from './MachineFill';
import { Opener, useVerInvalidate } from './VerSections';

const STATUS_LABELS = { planned: 'planejada', done: 'realizada', cancelled: 'cancelada' } as const;
const SIDE_LABELS = { raro: 'Raro', client: 'cliente', external: 'externo' } as const;

/**
 * SESSÕES do VER: kickoff, imersão, entrevista, visita, observação de loja, conversa com vendedor…
 * Flexíveis (premissa 5). Cada sessão é um contêiner de participantes e fontes.
 * A Machine propõe a pauta antes e resume depois; o que ela destaca vira sugestão para julgamento.
 */
export function SessionsSection({ projectId, organizationId, canEdit }: { projectId: string; organizationId: string; canEdit: boolean }) {
  const invalidate = useVerInvalidate(projectId);
  const list = useQuery({ queryKey: ['sessions', projectId], queryFn: () => api.get<Session[]>(`/projects/${projectId}/sessions`) });
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ kind: 'interview' as SessionKind, title: '', objective: '', location: '', remote: false, scheduledAt: '', participants: '' });
  const create = useMutation({
    mutationFn: () =>
      api.post(`/projects/${projectId}/sessions`, {
        kind: f.kind,
        title: f.title,
        objective: f.objective || undefined,
        location: f.location || undefined,
        remote: f.remote,
        scheduledAt: f.scheduledAt ? new Date(f.scheduledAt).toISOString() : undefined,
        participants: parseParticipants(f.participants),
      }),
    onSuccess: () => { setOpen(false); setF({ kind: 'interview', title: '', objective: '', location: '', remote: false, scheduledAt: '', participants: '' }); invalidate(); },
  });
  const planned = list.data?.filter((s) => s.status === 'planned') ?? [];
  const done = list.data?.filter((s) => s.status !== 'planned') ?? [];
  return (
    <section className="rr-section">
      <div className="rr-section__head">
        <Label>Sessões</Label>
        <span className="rr-faint" style={{ fontSize: 'var(--rr-text-xs)' }}>onde o VER acontece: sócios e colaboradores do cliente, campo, loja, fábrica, consumidor. a Machine prepara a pauta e resume o que a sessão mostrou.</span>
      </div>
      {canEdit && (
        <Opener open={open} onOpen={() => setOpen(true)} label="+ nova sessão">
          <MachineFill<{ kind: SessionKind; title: string; objective: string; participants: SessionParticipant[]; location: string | null; remote: boolean }> form="session" projectId={projectId} placeholder="ex.: quinta vamos observar a loja do Bom Retiro com a diretora e a vendedora Cláudia" onFill={(r) => setF({ ...f, kind: r.kind ?? f.kind, title: r.title || f.title, objective: r.objective || f.objective, location: r.location ?? f.location, remote: Boolean(r.remote), participants: r.participants?.length ? r.participants.map((p) => `${p.name} · ${p.role ?? ''} · ${p.side === 'raro' ? 'raro' : p.side === 'client' ? 'cliente' : 'externo'}`).join('\n') : f.participants })} />
          <div className="rr-row" style={{ gap: 'var(--rr-space-4)', alignItems: 'flex-start' }}>
            <Field label="Tipo" htmlFor="s-k"><Select id="s-k" value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value as SessionKind })}>{SESSION_KINDS.map((k) => <option key={k} value={k}>{SESSION_KIND_LABELS[k]}</option>)}</Select></Field>
            <Field label="Quando" htmlFor="s-d"><Input id="s-d" type="datetime-local" value={f.scheduledAt} onChange={(e) => setF({ ...f, scheduledAt: e.target.value })} /></Field>
          </div>
          <Field label="Nome" htmlFor="s-t"><Input id="s-t" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="kickoff com a diretoria, visita à fábrica, loja do Bom Retiro…" autoFocus /></Field>
          <Field label="O que queremos ver nesta sessão?" htmlFor="s-o"><Textarea id="s-o" rows={2} value={f.objective} onChange={(e) => setF({ ...f, objective: e.target.value })} /></Field>
          <div className="rr-row" style={{ gap: 'var(--rr-space-4)', alignItems: 'flex-end' }}>
            <Field label="Onde" htmlFor="s-l"><Input id="s-l" value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} placeholder="endereço, loja, sala" /></Field>
            <label className="rr-row" style={{ gap: 'var(--rr-space-2)', fontSize: 'var(--rr-text-sm)', paddingBottom: 10 }}><input type="checkbox" checked={f.remote} onChange={(e) => setF({ ...f, remote: e.target.checked })} /> remota</label>
          </div>
          <Field label="Participantes" htmlFor="s-p" hint="um por linha: nome · papel · raro/cliente/externo">
            <Textarea id="s-p" rows={3} value={f.participants} onChange={(e) => setF({ ...f, participants: e.target.value })} placeholder={'Tiago · sócio · raro\nMaria · diretora comercial · cliente\nCláudia · vendedora · externo'} />
          </Field>
          {create.isError && <Notice tone="error">{(create.error as Error).message}</Notice>}
          <div className="rr-row">
            <Button variant="primary" disabled={f.title.trim().length < 2 || create.isPending} onClick={() => create.mutate()}>criar sessão</Button>
            <Button variant="quiet" onClick={() => setOpen(false)}>cancelar</Button>
          </div>
        </Opener>
      )}
      {list.isSuccess && list.data.length === 0 && <EmptyState title="nenhuma sessão ainda.">o VER começa reunindo quem decide e quem vive o problema. kickoff, imersão, campo.</EmptyState>}
      {planned.length > 0 && <div className="rr-stack rr-stack--3"><Label tone="strong">Próximas</Label>{planned.map((s) => <SessionCard key={s.id} s={s} projectId={projectId} organizationId={organizationId} canEdit={canEdit} />)}</div>}
      {done.length > 0 && <div className="rr-stack rr-stack--3"><Label tone="strong">Realizadas</Label>{done.map((s) => <SessionCard key={s.id} s={s} projectId={projectId} organizationId={organizationId} canEdit={canEdit} />)}</div>}
    </section>
  );
}

function parseParticipants(text: string): SessionParticipant[] {
  return text
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean)
    .map((l) => {
      const [name = '', role = '', side = ''] = l.split('·').map((x) => x.trim());
      const s = side.toLowerCase();
      return { name, role: role || undefined, side: s.startsWith('r') ? 'raro' : s.startsWith('c') ? 'client' : s.startsWith('e') ? 'external' : 'client' } as SessionParticipant;
    });
}

function SessionCard({ s, projectId, organizationId, canEdit }: { s: Session; projectId: string; organizationId: string; canEdit: boolean }) {
  const invalidate = useVerInvalidate(projectId);
  const status = useAiStatus();
  const llm = Boolean(status.data?.llm);
  const [expanded, setExpanded] = useState(false);
  const [summary, setSummary] = useState(s.summary ?? '');
  const [notes, setNotes] = useState(s.notes ?? '');
  const [agenda, setAgenda] = useState(s.agenda ?? '');
  const [attaching, setAttaching] = useState(false);
  const [adding, setAdding] = useState(false);
  const detail = useQuery({
    queryKey: ['session', s.id],
    queryFn: () => api.get<SessionDetail>(`/sessions/${s.id}`),
    enabled: expanded,
    refetchInterval: (q) => (q.state.data?.sources.some((x) => sourceIsPending(x.status as Source['status'])) ? 2500 : false),
  });
  const free = useQuery({ queryKey: ['sources', organizationId, projectId], queryFn: () => api.get<Source[]>(`/organizations/${organizationId}/sources?projectId=${projectId}`), enabled: attaching });
  const update = useMutation({ mutationFn: (body: Record<string, unknown>) => api.patch(`/sessions/${s.id}`, body), onSuccess: invalidate });
  const attach = useMutation({ mutationFn: (sourceId: string) => api.post(`/sessions/${s.id}/sources/${sourceId}`), onSuccess: () => { setAttaching(false); invalidate(); } });
  const suggestAgenda = useMutation({ mutationFn: () => api.post<Session & { model: string }>(`/sessions/${s.id}/agenda`), onSuccess: (r) => { setAgenda(r.agenda ?? ''); setExpanded(true); invalidate(); } });
  const summarize = useMutation({ mutationFn: () => api.post<Session & { suggestions: unknown[]; model: string; latencyMs: number }>(`/sessions/${s.id}/summarize`), onSuccess: (r) => { setSummary(r.summary ?? ''); setExpanded(true); invalidate(); } });
  const when = s.heldAt ?? s.scheduledAt;
  const d = detail.data;
  const aiSummary = s.summaryGeneratedBy === 'ai';
  return (
    <article className="rr-session">
      <div className="rr-row rr-row--between" style={{ alignItems: 'flex-start' }}>
        <button type="button" className="rr-session__head" onClick={() => setExpanded(!expanded)}>
          <div className="rr-row" style={{ gap: 'var(--rr-space-2)' }}>
            <Tag tone={s.status === 'done' ? 'accent' : 'outline'}>{SESSION_KIND_LABELS[s.kind]}</Tag>
            <span className="rr-session__title">{s.title}</span>
          </div>
          <div className="rr-row rr-row--wrap" style={{ fontSize: 'var(--rr-text-xs)', color: 'var(--rr-text-3)', gap: 'var(--rr-space-3)' }}>
            {when && <span>{formatDate(when)}</span>}
            {s.location && <span>{s.location}</span>}
            {s.remote && <span>remota</span>}
            <span>{s.participants.length} participante(s)</span>
            <span>{s.sourceCount} fonte(s)</span>
            {s.agenda && <span>pauta pronta</span>}
            <span>{STATUS_LABELS[s.status]}</span>
          </div>
        </button>
        {canEdit && (
          <div className="rr-row" style={{ flexShrink: 0 }}>
            {s.status === 'planned' && <Button size="sm" variant="quiet" onClick={() => suggestAgenda.mutate()} disabled={!llm || suggestAgenda.isPending} title={llm ? undefined : 'sem modelo configurado'}>{suggestAgenda.isPending ? 'a Machine está preparando…' : s.agenda ? 'refazer pauta' : 'pauta pela Machine'}</Button>}
            {s.status === 'planned' && <Button size="sm" variant="primary" onClick={() => update.mutate({ status: 'done' })} disabled={update.isPending}>realizada</Button>}
            {s.status === 'done' && <Button size="sm" variant={s.summary ? 'quiet' : 'primary'} onClick={() => summarize.mutate()} disabled={!llm || summarize.isPending} title={llm ? undefined : 'sem modelo configurado'}>{summarize.isPending ? 'a Machine está lendo a sessão…' : s.summary ? 'resumir de novo' : 'resumir pela Machine'}</Button>}
          </div>
        )}
      </div>
      {(suggestAgenda.isError || summarize.isError) && <Notice tone="error">{((suggestAgenda.error ?? summarize.error) as Error).message}</Notice>}
      {summarize.isSuccess && <Notice>{summarize.data.suggestions.length} destaque(s) e fala(s) em {(summarize.data.latencyMs / 1000).toFixed(1)}s com {summarize.data.model}. nada vira fato sem você: <Link to={`/projetos/${projectId}?s=machine`}>julgar na Machine →</Link></Notice>}
      {s.objective && <p className="rr-muted" style={{ fontSize: 'var(--rr-text-sm)' }}>{s.objective}</p>}
      {s.participants.length > 0 && (
        <div className="rr-row rr-row--wrap" style={{ gap: 'var(--rr-space-2)' }}>
          {s.participants.map((p, i) => <span key={i} className={`rr-chip rr-chip--${p.side}`}>{p.name}{p.role ? ` · ${p.role}` : ''} <small>{SIDE_LABELS[p.side]}</small></span>)}
        </div>
      )}
      {s.summary && !expanded && <p className="rr-session__summary">{s.summary.split('\n')[0]}{aiSummary && <span className="rr-faint" style={{ fontFamily: 'var(--rr-font-sans)', fontStyle: 'normal', fontSize: 'var(--rr-text-xs)' }}> · resumo da Machine</span>}</p>}
      {expanded && (
        <div className="rr-stack rr-stack--5" style={{ borderTop: '1px solid var(--rr-border)', paddingTop: 'var(--rr-space-4)' }}>
          {(s.agenda || s.status === 'planned') && (
            <div className="rr-stack rr-stack--2">
              <Label>Pauta</Label>
              {canEdit ? (
                <Textarea rows={Math.min(14, Math.max(3, agenda.split('\n').length + 1))} value={agenda} placeholder="o que perguntar, o que observar, o que provocar. ou peça a pauta pela Machine." onChange={(e) => setAgenda(e.target.value)} onBlur={() => agenda !== (s.agenda ?? '') && update.mutate({ agenda })} className={cx(agenda && 'rr-session__agenda-text')} />
              ) : (
                s.agenda && <p className="rr-session__agenda">{s.agenda}</p>
              )}
            </div>
          )}

          <div className="rr-stack rr-stack--2">
            <div className="rr-row rr-row--between">
              <Label>Fontes da sessão · {d?.sources.length ?? s.sourceCount}</Label>
              {canEdit && (
                <div className="rr-row">
                  <Button size="sm" variant="primary" onClick={() => setAdding(true)}>+ gravação, fotos ou notas</Button>
                  {!attaching && <Button size="sm" variant="quiet" onClick={() => setAttaching(true)}>ligar fonte existente</Button>}
                </div>
              )}
            </div>
            {attaching && (
              <div className="rr-row">
                <Select value="" onChange={(e) => e.target.value && attach.mutate(e.target.value)} style={{ width: 'auto' }}>
                  <option value="">— fonte sem sessão —</option>
                  {free.data?.filter((x) => !x.sessionId).map((x) => <option key={x.id} value={x.id}>{x.title}</option>)}
                </Select>
                <Button size="sm" variant="quiet" onClick={() => setAttaching(false)}>fechar</Button>
              </div>
            )}
            {d && d.sources.length === 0 && <p className="rr-faint" style={{ fontSize: 'var(--rr-text-xs)' }}>nenhuma fonte ligada. a gravação da conversa, as fotos da loja e as notas de campo entram aqui; a Machine transcreve, fragmenta e extrai candidatas a evidência.</p>}
            {d?.sources.map((x) => (
              <Link key={x.id} to={`/fontes/${x.id}`} className="rr-list-row" style={{ gridTemplateColumns: '1fr auto' }}>
                <div>
                  <div className="rr-list-row__title">{x.title}</div>
                  <div className="rr-list-row__meta"><span>{SOURCE_TYPE_LABELS[x.type as Source['type']] ?? x.type}</span><span>{formatDate(x.createdAt)}</span></div>
                </div>
                <Tag tone={x.status === 'processed' ? 'outline' : 'accent'}>{SOURCE_STATUS_LABELS[x.status as Source['status']] ?? x.status}</Tag>
              </Link>
            ))}
          </div>

          <ConversationsPanel organizationId={organizationId} projectId={projectId} sessionId={s.id} compact />

          {d && (d.evidence.length > 0 || d.suggestionsPending > 0) && (
            <div className="rr-stack rr-stack--2">
              <div className="rr-row rr-row--between">
                <Label>O que saiu desta sessão</Label>
                {d.suggestionsPending > 0 && <Link to={`/projetos/${projectId}?s=machine`} className="rr-action rr-action--bare" style={{ fontSize: 'var(--rr-text-xs)' }}>{d.suggestionsPending} sugestão(ões) da Machine a julgar →</Link>}
              </div>
              {d.evidence.map((e) => (
                <div key={e.id} className="rr-list-row" style={{ gridTemplateColumns: '1fr auto' }}>
                  <div>
                    <div className="rr-list-row__title" style={{ fontFamily: 'var(--rr-font-serif)', fontStyle: 'italic' }}>“{e.content}”</div>
                    <div className="rr-list-row__meta"><span>evidência</span>{e.note && <span>{e.note}</span>}<span>{e.generatedBy === 'ai' ? 'destacada pela Machine' : 'marcada por pessoa'}</span></div>
                  </div>
                  <Tag tone={e.humanStatus === 'accepted' ? 'accent' : 'outline'}>{e.humanStatus === 'accepted' ? 'aceita' : e.humanStatus === 'rejected' ? 'rejeitada' : 'em revisão'}</Tag>
                </div>
              ))}
            </div>
          )}

          <div className="rr-stack rr-stack--2">
            <div className="rr-row rr-row--between">
              <Label>O que a sessão mostrou</Label>
              {aiSummary && <span className="rr-faint" style={{ fontSize: 'var(--rr-text-xs)' }}>resumo da Machine · edite à vontade</span>}
            </div>
            {canEdit ? (
              <Textarea rows={Math.min(8, Math.max(2, summary.split('\n').length + 1))} value={summary} placeholder="o que esta sessão mostrou, em uma ou duas frases. ou peça o resumo pela Machine." onChange={(e) => setSummary(e.target.value)} onBlur={() => summary !== (s.summary ?? '') && update.mutate({ summary })} />
            ) : (
              s.summary && <p style={{ whiteSpace: 'pre-wrap' }}>{s.summary}</p>
            )}
          </div>

          <div className="rr-stack rr-stack--2">
            <Label>Notas de quem esteve lá</Label>
            {canEdit ? (
              <Textarea rows={4} value={notes} placeholder="o que você viu e ouviu. entra no resumo da Machine." onChange={(e) => setNotes(e.target.value)} onBlur={() => notes !== (s.notes ?? '') && update.mutate({ notes })} />
            ) : (
              s.notes && <p className="rr-muted" style={{ whiteSpace: 'pre-wrap' }}>{s.notes}</p>
            )}
          </div>
          {canEdit && s.status !== 'cancelled' && <Button size="sm" variant="quiet" onClick={() => update.mutate({ status: 'cancelled' })} style={{ alignSelf: 'flex-start' }}>cancelar sessão</Button>}
        </div>
      )}
      <AddSourceDrawer open={adding} onClose={() => setAdding(false)} organizationId={organizationId} projectId={projectId} sessionId={s.id} eyebrow={`Sessão · ${s.title}`} />
    </article>
  );
}
